/** @file Allocates RenderBuffers with capacity derived from SkeletonData (never reallocates per frame). */
import { invariant } from '@pose2d/std/invariant';
import type { AttachmentData, SkeletonData } from '@pose2d/core/types';
import type { Pose2DError } from '@pose2d/format/types';
import { RENDER_LAYOUT, type RenderBuffers } from './renderBuffers';

const MAX = RENDER_LAYOUT.MAX_VERTICES;

/** [vertices, indices] one attachment emits. */
const sizeOf = (att: AttachmentData): [number, number] => (att.type === 'region' ? [4, 6] : [att.uvs.length / 2, att.triangles.length]);

/** Attachment indices slot `slot` can show: its entries in every skin, plus handle-bearing ones on socket bones (§5.9). */
function candidates(data: SkeletonData, slot: number): number[] {
  const out: number[] = [];
  for (const skin of data.skins) out.push(...(skin.map.get(slot)?.values() ?? []));
  const bone = data.bones[data.slots[slot]?.bone ?? -1];
  if (bone !== undefined && bone.kind !== 'bone') {
    data.attachments.forEach((att, a) => {
      if (att.handle !== null) out.push(a);
    });
  }
  return out;
}

/** A non-negative integer extra (default 0). */
function extra(value: number | undefined, name: string): number {
  const n = value ?? 0;
  invariant(Number.isInteger(n) && n >= 0, `createRenderBuffers: ${name} must be a non-negative integer extra`);
  return n;
}

/**
 * Throws VALIDATION as a Pose2DError (`.pose2d`, read back with format's toPose2DError; geometry imports format as
 * types only): `vertices` exceed the 16-bit index space, `culprit` names what took them there.
 */
function tooManyVertices(vertices: number, culprit: { attachment: string; slot: string; count: number } | null): never {
  const message = `createRenderBuffers: ${vertices} vertices exceed RENDER_LAYOUT.MAX_VERTICES (${MAX})`;
  const hint = culprit === null
    ? `the extraVertices alone exceed what 16-bit render indices reach (${MAX})`
    : `attachment "${culprit.attachment}" on slot "${culprit.slot}" (${culprit.count} vertices) takes what this skeleton can draw at once to ${vertices} vertices; 16-bit render indices reach ${MAX}`;
  const pose2d: Pose2DError = { code: 'VALIDATION', message, ...(culprit === null ? {} : { params: { attachment: culprit.attachment, slot: culprit.slot } }), hint };
  throw Object.assign(new Error(`${message}: ${hint}`), { pose2d });
}

/**
 * Capacity = max attachment per slot over all skins (+ handle-bearing attachments for socket slots) + extras.
 * Vertex and index maxima are taken per slot independently; each slot that can show an attachment with vertices adds
 * one draw and one run (`extraDraws` sizes both tables; buildRender draws nothing for an attachment without vertices).
 * More than RENDER_LAYOUT.MAX_VERTICES vertices is VALIDATION (a Pose2DError naming the attachment that crossed it).
 */
export function createRenderBuffers(data: SkeletonData, opts?: { extraVertices?: number; extraIndices?: number; extraDraws?: number }): RenderBuffers {
  let vertices = extra(opts?.extraVertices, 'extraVertices');
  let indices = extra(opts?.extraIndices, 'extraIndices');
  let tables = extra(opts?.extraDraws, 'extraDraws');
  if (vertices > MAX) tooManyVertices(vertices, null);
  data.slots.forEach((slotData, slot) => {
    let maxV = 0;
    let maxI = 0;
    let largest = -1;
    for (const a of candidates(data, slot)) {
      const att = data.attachments[a];
      if (att === undefined) continue;
      const [v, i] = sizeOf(att);
      if (v > maxV) largest = a;
      maxV = Math.max(maxV, v);
      maxI = Math.max(maxI, i);
    }
    vertices += maxV;
    indices += maxI;
    if (maxV > 0) tables += 1;
    if (vertices > MAX) tooManyVertices(vertices, { attachment: data.attachments[largest]?.name ?? '', slot: slotData.name, count: maxV });
  });
  const buffer = new ArrayBuffer(vertices * RENDER_LAYOUT.VERTEX_WORDS * 4);
  return {
    vertexF32: new Float32Array(buffer),
    vertexU32: new Uint32Array(buffer),
    indices: new Uint16Array(indices),
    draws: new Int32Array(tables * RENDER_LAYOUT.DRAW_STRIDE),
    runs: new Int32Array(tables * RENDER_LAYOUT.RUN_STRIDE),
    vertexCount: 0,
    indexCount: 0,
    drawCount: 0,
    runCount: 0,
    version: 0,
    topologyVersion: 0,
    bounds: new Float32Array(4),
  };
}
